const MOBILE_BREAKPOINT = 749;
const SLOT_COUNT = 3;

function parseNumber(value, fallback) {
  const parsed = Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

class CHPolaroidLine extends HTMLElement {
  #gallery = [];
  #cards = [];
  #timeouts = new Set();
  #intersectionObserver = null;
  #isActive = false;

  connectedCallback() {
    const allCards = Array.from(this.querySelectorAll("[data-polaroid-card]"));

    this.#gallery = this.#readGallery();
    this.#cards = allCards.slice(0, SLOT_COUNT);

    if (!this.#gallery.length) {
      this.#gallery = this.#readRenderedImages(allCards);
    }

    if (!this.#cards.length || !this.#gallery.length) {
      return;
    }

    const shouldReduceMotion = this.#shouldReduceMotion();

    this.#cards.forEach((card, index) => {
      const currentImage = card.querySelector("[data-polaroid-current]");
      const nextImage = card.querySelector("[data-polaroid-next]");
      const currentIndex = index % this.#gallery.length;

      card.dataset.currentIndex = String(currentIndex);
      card.dataset.nextIndex = String(this.#advanceIndex(currentIndex));
      this.#applyImage(currentImage, this.#gallery[currentIndex], true);
      this.#applyPostDetails(card, this.#gallery[currentIndex]);

      if (nextImage) {
        this.#clearImage(nextImage);
      }
    });

    if (this.#shouldReduceSway()) {
      this.dataset.motionMode = "static";
    }

    if (this.dataset.animatePolaroids === "false" || shouldReduceMotion || this.#gallery.length <= 1) {
      this.dataset.motionMode = "static";
      return;
    }

    if (!this.#shouldReduceSway()) {
      this.dataset.motionMode = "animated";
    }
    this.#setupVisibilityObserver();
    document.addEventListener("visibilitychange", this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    this.#stop();
    this.#intersectionObserver?.disconnect();
    document.removeEventListener("visibilitychange", this.#handleVisibilityChange);
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
      return;
    }

    this.#start();
  };

  #readGallery() {
    const nodes = Array.from(this.querySelectorAll("[data-polaroid-gallery]"));
    if (!nodes.length) return [];

    return nodes.flatMap((node) => {
      try {
        const parsed = JSON.parse(node.textContent || "[]");
        return Array.isArray(parsed) ? parsed.filter((item) => item?.src) : [];
      } catch {
        return [];
      }
    });
  }

  #readRenderedImages(cards) {
    return cards
      .map((card) => {
        const image = card.querySelector("[data-polaroid-current]");
        const src = image?.currentSrc || image?.src || image?.getAttribute("src");
        if (!src) return null;

        return {
          src,
          alt: image.alt || "",
          title: card.querySelector("[data-polaroid-caption]")?.textContent?.trim() || "",
          link: card.querySelector("[data-polaroid-link]")?.getAttribute("href") || "",
        };
      })
      .filter(Boolean);
  }

  #shouldReduceMotion() {
    return prefersReducedMotion();
  }

  #shouldReduceSway() {
    return (
      this.dataset.mobileMotionReduction === "true" &&
      window.innerWidth <= MOBILE_BREAKPOINT
    );
  }

  #setupVisibilityObserver() {
    if (!("IntersectionObserver" in window)) {
      this.#start();
      return;
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);

        if (visible) {
          this.#start();
        } else {
          this.#stop();
        }
      },
      { threshold: 0.1 },
    );

    this.#intersectionObserver.observe(this);
  }

  #start() {
    if (this.#isActive || document.hidden) return;

    this.#isActive = true;
    const stagger = parseNumber(this.dataset.cycleStagger, 1.5) * 1000;
    const initialHold = parseNumber(this.dataset.holdDuration, 5) * 1000;

    this.#cards.forEach((card, index) => {
      this.#setTimeout(
        () => this.#runCycle(card),
        Math.max(0, initialHold + index * stagger * 0.66),
      );
    });
  }

  #stop() {
    this.#isActive = false;
    this.#timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#timeouts.clear();

    this.#cards.forEach((card) => {
      card.classList.remove("is-developing");

      const nextImage = card.querySelector("[data-polaroid-next]");
      this.#clearImage(nextImage);
    });
  }

  #setTimeout(callback, delay) {
    const timeoutId = window.setTimeout(() => {
      this.#timeouts.delete(timeoutId);
      callback();
    }, delay);

    this.#timeouts.add(timeoutId);
  }

  #advanceIndex(index) {
    return (index + this.#cards.length) % this.#gallery.length;
  }

  #applyImage(imageElement, entry, eager = false) {
    if (!imageElement || !entry) return;

    if (entry.srcset) {
      imageElement.srcset = entry.srcset;
      imageElement.sizes = entry.sizes || "(min-width: 990px) 24vw, 70vw";
    } else {
      imageElement.removeAttribute("srcset");
      imageElement.removeAttribute("sizes");
    }

    imageElement.loading = eager ? "eager" : "lazy";
    imageElement.setAttribute("fetchpriority", "low");
    imageElement.decoding = "async";
    imageElement.setAttribute("width", entry.width || "900");
    imageElement.setAttribute("height", entry.height || "1023");
    imageElement.src = entry.src;
    imageElement.alt = entry.alt || "";
  }

  #clearImage(imageElement) {
    if (!imageElement) return;

    imageElement.removeAttribute("src");
    imageElement.removeAttribute("srcset");
    imageElement.removeAttribute("sizes");
    imageElement.alt = "";
  }

  #applyPostDetails(card, entry) {
    const link = card.querySelector("[data-polaroid-link]");
    const caption = card.querySelector("[data-polaroid-caption]");
    const title = entry?.title?.trim() || card.dataset.polaroidFallbackTitle || "";

    if (caption) {
      caption.textContent = title;
      caption.toggleAttribute("hidden", !title);
    }

    if (!link) return;

    if (entry?.link) {
      link.href = entry.link;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.setAttribute("aria-label", title || entry.alt || entry.link);
    } else {
      link.removeAttribute("href");
      link.removeAttribute("target");
      link.removeAttribute("rel");
      link.removeAttribute("aria-label");
    }
  }

  #runCycle(card) {
    if (!this.#isActive) return;

    const currentImage = card.querySelector("[data-polaroid-current]");
    const nextImage = card.querySelector("[data-polaroid-next]");
    if (!currentImage || !nextImage) return;

    const nextIndex = parseInt(card.dataset.nextIndex || "0", 10) % this.#gallery.length;
    const nextEntry = this.#gallery[nextIndex];
    this.#applyImage(nextImage, nextEntry);

    const developDuration = parseNumber(this.dataset.developDuration, 4) * 1000;
    const cycleDuration = developDuration;
    const blueCoverDuration = Math.min(Math.max(cycleDuration * 0.22, 520), 1100);
    const holdDuration = parseNumber(this.dataset.holdDuration, 5) * 1000;

    this.#whenImageReady(nextImage, () => {
      if (!this.#isActive) return;

      card.style.setProperty(
        "--ch-polaroid-current-develop-duration",
        `${cycleDuration / 1000}s`,
      );
      card.classList.remove("is-developing");
      void card.offsetWidth;
      card.classList.add("is-developing");

      this.#setTimeout(() => {
        if (!this.#isActive) return;

        const committedIndex = nextIndex;
        this.#applyImage(currentImage, nextEntry);
        this.#applyPostDetails(card, nextEntry);
        card.dataset.currentIndex = String(committedIndex);
        card.dataset.nextIndex = String(this.#advanceIndex(committedIndex));
      }, blueCoverDuration);

      this.#setTimeout(() => {
        this.#clearImage(nextImage);
        card.classList.remove("is-developing");

        if (this.#isActive) {
          this.#setTimeout(() => this.#runCycle(card), holdDuration);
        }
      }, cycleDuration);
    });
  }

  #whenImageReady(imageElement, callback) {
    if (imageElement.complete && imageElement.naturalWidth > 0) {
      requestAnimationFrame(callback);
      return;
    }

    const onLoad = () => {
      imageElement.removeEventListener("load", onLoad);
      imageElement.removeEventListener("error", onLoad);
      requestAnimationFrame(callback);
    };

    imageElement.addEventListener("load", onLoad, { once: true });
    imageElement.addEventListener("error", onLoad, { once: true });
  }
}

if (!customElements.get("ch-polaroid-line")) {
  customElements.define("ch-polaroid-line", CHPolaroidLine);
}
